import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Карта маршрутов курьеров: Leaflet + тайлы OpenStreetMap. Без ключей и без
// ограничений на закрытые системы и мониторинг в реальном времени (бесплатный
// Яндекс JS API это запрещает п. 4.1.1 и 5.1.3 условий, коммерческий — 195 000 ₽/год;
// решение 2026-08-29). Провайдер тайлов — одна константа ниже: при нехватке
// детализации меняется на 2ГИС (платно, по тайлам). Данные приходят готовыми:
// геометрия [[lat, lon]] и ETA считает OSRM на бэкенде, карта только рисует.

export const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
export const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors';
const DEFAULT_CENTER = [55.7887, 49.1221];

export const CLUSTER_COLORS = ['#26689A', '#1F763B', '#EF6C00', '#7B1FA2', '#CB4D45', '#00838F', '#6D4C41', '#AD1457'];
export const clusterColor = (i) => CLUSTER_COLORS[Math.abs(i) % CLUSTER_COLORS.length];

function esc(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Маркер — наш DOM внутри пустой (0×0) обёртки Leaflet: .ca-map-marker сам центрируется
// на точке через translate(-50%, -50%), стили — в CourierAssignmentPage.css
function pinIcon(label, color, { title = '', muted = false, className = 'ca-map-marker' } = {}) {
  const style = color ? ` style="background:${esc(color)}"` : '';
  return L.divIcon({
    className: 'ca-map-pin',
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html: `<div class="${className}${muted ? ' muted' : ''}"${style} title="${esc(title)}">${esc(label)}</div>`,
  });
}

/**
 * props:
 *   warehouse  {lat, lon}
 *   clusters   [{ id, color, label, orders: [{id, order_number, delivery_lat, delivery_lon, delivery_sequence, status}], geometry: [[lat, lon]] }]
 *   points     [{id, order_number, delivery_lat, delivery_lon}] — заказы без группы (серые)
 *   couriers   [{id, name, lat, lon, age_s, stale}] — живые позиции
 *   onOrderClick(orderId)
 *   fitKey     — при смене подгоняет границы под точки (а не на каждом обновлении)
 */
export default function RouteMap({ warehouse, clusters = [], points = [], couriers = [], onOrderClick, fitKey = 0, height = 420, highlightId = null }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const fittedKeyRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    const center = warehouse && warehouse.lat ? [warehouse.lat, warehouse.lon] : DEFAULT_CENTER;
    // attributionControl: false + свой контроль без префикса — Leaflet ≥1.9 в префикс
    // «Leaflet» добавляет флаг; подпись OSM остаётся (требование лицензии ODbL)
    // Зум — внизу слева: верхний левый угол занят плавающей легендой групп
    const map = L.map(containerRef.current, { center, zoom: 11, attributionControl: false, zoomControl: false });
    L.control.zoom({ position: 'bottomleft' }).addTo(map);
    L.control.attribution({ prefix: false }).addTo(map);
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const bounds = [];

    if (warehouse && warehouse.lat && warehouse.lon) {
      L.marker([warehouse.lat, warehouse.lon], {
        icon: pinIcon('С', '#1C3D2E', { title: 'Склад', className: 'ca-map-marker warehouse' }),
        zIndexOffset: 500,
        interactive: false,
      }).addTo(layer);
      bounds.push([warehouse.lat, warehouse.lon]);
    }

    clusters.forEach((cl) => {
      // Подсветка группы по наведению в списке: остальные приглушаются
      const dim = highlightId != null && cl.id !== highlightId;
      const lit = highlightId != null && cl.id === highlightId;
      if (Array.isArray(cl.geometry) && cl.geometry.length > 1) {
        L.polyline(cl.geometry, { color: cl.color, weight: lit ? 6 : 4, opacity: dim ? 0.2 : 0.9, lineJoin: 'miter' }).addTo(layer);
      }
      (cl.orders || []).forEach((o) => {
        if (!o.delivery_lat || !o.delivery_lon) return;
        const marker = L.marker([o.delivery_lat, o.delivery_lon], {
          icon: pinIcon(o.delivery_sequence != null ? String(o.delivery_sequence) : '•', cl.color, {
            title: `#${o.order_number} · ${o.delivery_time || ''}${o.estimated_arrival ? ` · ~${o.estimated_arrival}` : ''}`,
            muted: o.status === 'completed',
            className: 'ca-map-marker' + (dim ? ' dim' : ''),
          }),
          zIndexOffset: lit ? 300 : 100,
        }).addTo(layer);
        if (onOrderClick) marker.on('click', () => onOrderClick(o.id));
        bounds.push([o.delivery_lat, o.delivery_lon]);
      });
    });

    points.forEach((o) => {
      if (!o.delivery_lat || !o.delivery_lon) return;
      const marker = L.marker([o.delivery_lat, o.delivery_lon], {
        icon: pinIcon('•', null, { title: `#${o.order_number} · ${o.delivery_time || ''}`, className: 'ca-map-marker point' }),
        zIndexOffset: 50,
      }).addTo(layer);
      if (onOrderClick) marker.on('click', () => onOrderClick(o.id));
      bounds.push([o.delivery_lat, o.delivery_lon]);
    });

    couriers.forEach((c) => {
      if (!c.lat || !c.lon) return;
      // Подпись — имя целиком (инициал «Э» читался как «3»); точка слева — сама позиция
      const age = c.age_s != null ? ` · ${Math.round(c.age_s / 60)} мин назад` : '';
      L.marker([c.lat, c.lon], {
        icon: pinIcon(c.name || 'курьер', null, { title: `${c.name}${age}`, className: 'ca-map-courier' + (c.stale || c.age_s > 600 ? ' stale' : '') }),
        zIndexOffset: 1000,
        interactive: false,
      }).addTo(layer);
    });

    // Контейнер мог быть скрыт/изменить размер — Leaflet должен пересчитать вьюпорт
    map.invalidateSize();

    if (fittedKeyRef.current !== fitKey && bounds.length > 0) {
      fittedKeyRef.current = fitKey;
      if (bounds.length === 1) {
        map.setView(bounds[0], 13);
      } else {
        map.fitBounds(bounds, { padding: [28, 28], maxZoom: 15 });
      }
    }
  }, [warehouse, clusters, points, couriers, onOrderClick, fitKey, highlightId]);

  return <div ref={containerRef} className="ca-map" style={{ height }} />;
}
